import PropTypes from "prop-types";
import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "@gatsbyjs/reach-router";

import { CartContext } from "./CartProvider";
import { getAllUrlParams } from "@helpers/browser";
import { useAuth } from "./AuthProvider";
import { useTracking } from "./Tracking";
import { sendALErrorReport, withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { GeoContext } from "./GeoProvider";
import { Country } from "../constants/Country";
import { getLuxeGiftInfo, LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY } from "@helpers/luxe";
import { useProductData2 } from "./products";
import { HandleType } from "@constants/HandleType";

const LUXE_PERKS = [
  {
    id: "discount",
    text: "Up to {0}% off every order",
    availableWithChurnOffer: true,
  },
  {
    id: "solid-gold",
    text: "5% off solid gold & diamonds",
    availableWithChurnOffer: true,
  },
  {
    id: "birthday-gift",
    text: "$40 birthday gift card",
    availableWithChurnOffer: false,
  },
  {
    id: "luxe-gift",
    text: "Free gift after 2 months of membership",
    availableWithChurnOffer: false,
  },
  {
    id: "early-access",
    text: "Early access to sales",
    availableWithChurnOffer: true,
  },
  {
    id: "priority-service",
    text: "Priority customer service",
    availableWithChurnOffer: true,
  },
];

const defaultLuxeContext = {
  luxe: {
    extraDiscountTags: ["Components:10K Solid Gold", "Components:10K Solid White Gold"],
    isLuxeEnabled: false,
    isLuxeActive: false,
    isForcedGuest: false,
    shouldAddLuxeInNextATC: true,
    subscriptionPlan: null,
    customerSubscription: null,
    subscriptionCredit: null,
    hasLuxeInCart: false,
    accountDetailsLoaded: false,
    offers: null,
    hasActiveOffer: false,
    luxePerks: LUXE_PERKS,
  },
  setLuxeActive: () => {},
  addLuxeItemToCart: () => {},
  removeLuxeItemToCart: () => {},
  applyLuxeDiscountToProductPrice: () => {},
  updateSubscriptionStatus: () => {},
  acceptChurnOffer: () => {},
};

export const LuxeContext = createContext(defaultLuxeContext);

let luxeGiftATCAttempts = 0; // Extra protection to avoid infinite loops in case of unexpected issues with the gift product
let isLoadingAddLuxeGift = false;

function useAddLuxeGiftToCart(isLuxeGiftActive) {
  const { addSingleProductToCart } = useContext(CartContext);
  const productsToRetrieve = useMemo(() => [HandleType.LUXE_RETENTION_GIFT], []);
  const { data: luxeGiftProductData } = useProductData2(
    productsToRetrieve,
    "short",
    "US",
    isLuxeGiftActive
  );

  return useCallback(
    async (giftLineItemId) => {
      const luxeGiftProduct = luxeGiftProductData?.[0]?.node;
      if (!luxeGiftProduct || isLoadingAddLuxeGift) return null;
      const dataProductToAdd = {
        quantity: 1,
        attributes: [
          {
            key: LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY,
            value: "true",
          },
        ],
        variant: {
          id: luxeGiftProduct.variants[0].shopifyId,
          sku: luxeGiftProduct.variants[0].sku,
          product: {
            title: luxeGiftProduct.title,
            id: luxeGiftProduct.shopifyId,
            handle: luxeGiftProduct.handle,
            productType: luxeGiftProduct.category,
            images: luxeGiftProduct.images,
          },
        },
      };
      if (giftLineItemId) {
        // Special case in case the gift gets added multiple times (adjust quantity to 1 instead of adding a new line item)
        dataProductToAdd.id = giftLineItemId;
      }
      luxeGiftATCAttempts += 1;
      if (luxeGiftATCAttempts > 5) {
        sendALErrorReport(new Error("Too many attempts to add Luxe Gift to cart"), {
          name: "LuxeGiftATCError",
          priority: "P1",
          extraInfo: {
            attempts: luxeGiftATCAttempts,
            productData: dataProductToAdd,
          },
        });
        return null;
      }

      isLoadingAddLuxeGift = true;
      // delay by 1 second x attempts (no delay on first attempt)
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(addSingleProductToCart({ dataProductToAdd, shouldOpenSideCart: false }));
          isLoadingAddLuxeGift = false;
        }, 1000 * luxeGiftATCAttempts);
      });
    },
    [luxeGiftProductData, addSingleProductToCart]
  );
}

function buildLuxeProductDataToAdd(plan) {
  return {
    quantity: 1,
    attributes: [],
    variant: {
      id: `gid://shopify/ProductVariant/${plan.exison_plan_settings.value.product_variant_id}`,
      sku: "",
      // price: variant.price,
      // compareAtPrice: variant.compareAtPrice,
      sellingPlanId: `gid://shopify/SellingPlan/${plan.exison_plan_settings.value.plan_id}`,
      product: {
        title: plan.exison_plan_settings.value.product_name,
        id: `gid://shopify/Product/${plan.exison_plan_settings.value.product_gid}`,
        handle: "al-luxe",
        productType: "subscribfy_subscription_product",
      },
    },
  };
}

export function useAddToCartWithLuxe() {
  const { addManyProductsToCart } = useContext(CartContext);
  const {
    luxe: { shouldAddLuxeInNextATC, subscriptionPlan },
  } = useContext(LuxeContext);

  return useCallback(
    async (props) => {
      if (shouldAddLuxeInNextATC && subscriptionPlan) {
        props.dataProductsToAdd.push(buildLuxeProductDataToAdd(subscriptionPlan));
      }
      return addManyProductsToCart(props);
    },
    [addManyProductsToCart, shouldAddLuxeInNextATC, subscriptionPlan]
  );
}

function LuxeProvider({ subscription, children }) {
  const location = useLocation();
  const { accountDetails, isLoading: isLoadingAuth, pullAccountDetails } = useAuth();
  const { gePriceDetails } = useContext(GeoContext);
  const {
    cart,
    isLoadingCart,
    addSingleProductToCart,
    removeSingleProductsFromCart,
    setIsSideCartOpen,
    updateCartAttributes,
  } = useContext(CartContext);

  const [subscriptionCredit, setSubscriptionCredit] = useState(0);
  const [luxe, setLuxe] = useState(defaultLuxeContext.luxe);
  const [canAutoAddLuxeInCart, setCanAutoAddLuxeInCart] = useState(true);
  const [isForcedGuest, setIsForcedGuest] = useState(false);
  const [isLoadingChurnOffer, setIsLoadingChurnOffer] = useState(false);
  const [hasActiveOffer, setHasActiveOffer] = useState(
    luxe.customerSubscription?.offers?.length > 0
  );

  const { trackSubscriptionRefund, trackSubscriptionCancel } = useTracking();

  const countryCode = gePriceDetails?.CountryCode;

  const luxePerks = useMemo(
    () => (hasActiveOffer ? LUXE_PERKS.filter((perk) => perk.availableWithChurnOffer) : LUXE_PERKS),
    [hasActiveOffer]
  );

  useEffect(() => {
    if (accountDetails && accountDetails.metafields && accountDetails.metafields.length > 0) {
      const subscriptionMetafield = accountDetails?.metafields.find(
        (m) => m.namespace === "exison" && m.key === "customer_subscription1"
      )?.value;
      let customerSubscription =
        (subscriptionMetafield && JSON.parse(subscriptionMetafield)) || null;

      if (customerSubscription?.offers?.length > 0) {
        setHasActiveOffer(true);
        // calculate the new subscription price based on the offer
        const offer = customerSubscription.offers[0];
        const originalPrice = parseFloat(customerSubscription.price);
        const discountAmount =
          offer.discount_type === "percentage"
            ? (originalPrice * offer.discount_value) / 100
            : offer.discount_value;
        const newPrice = (originalPrice - discountAmount).toFixed(2);
        customerSubscription = {
          ...customerSubscription,
          discountedPrice: newPrice,
        };
      }

      if (
        customerSubscription &&
        customerSubscription?.scid &&
        !luxe.offers &&
        !isLoadingChurnOffer
      ) {
        // Fetch Churn Offers
        (async () => {
          try {
            setIsLoadingChurnOffer(true);
            const res = await axios.get("/.netlify/functions/subscription-churn", {
              params: {
                scid: customerSubscription.scid,
                countryCode,
              },
            });
            if (res.status === 200 && res.data) {
              setLuxe((prevLuxe) => ({
                ...prevLuxe,
                offers: res.data,
              }));
            }
          } catch (e) {
            sendALErrorReport(e, {
              name: "LuxeProviderFetchChurnOffers",
              priority: "P3",
            });
          } finally {
            setIsLoadingChurnOffer(false);
          }
        })();
      }

      setLuxe((prevLuxe) => ({
        ...prevLuxe,
        customerSubscription,
        accountDetailsLoaded: true,
      }));
    } else {
      // Reset Luxe (on logout)
      setLuxe(defaultLuxeContext.luxe);
    }
  }, [accountDetails, countryCode]);

  useEffect(() => {
    try {
      if (accountDetails && accountDetails.metafields) {
        const storeCredits = accountDetails.metafields.find(
          (m) => m.namespace === "exison" && m.key === "exison_st"
        );
        if (storeCredits) {
          setSubscriptionCredit(storeCredits.value);
        }
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  }, [accountDetails, countryCode]);

  // Store Metafield (for subscription)
  const subscriptionPlan = useMemo(
    () =>
      subscription.reduce((red, metafield) => {
        let data = {
          ...red,
        };
        if (metafield?.type === "json_string") {
          data = {
            ...red,
            [metafield.key]: {
              ...metafield,
              value: JSON.parse(metafield.value),
            },
          };
          delete data[metafield.key].key;
        }
        return data;
      }, {}),
    [subscription]
  );

  const addLuxeItemToCart = useCallback(
    async (plan, shouldOpenSideCart) => {
      if (plan) {
        setCanAutoAddLuxeInCart(true);
        const dataProductToAdd = buildLuxeProductDataToAdd(plan);
        return addSingleProductToCart({ dataProductToAdd, shouldOpenSideCart });
      }
    },
    [addSingleProductToCart]
  );

  const removeLuxeItemFromCart = useCallback(
    async (lineItem, shouldOpenSideCart) => {
      setCanAutoAddLuxeInCart(false);
      return removeSingleProductsFromCart({ lineToRemove: lineItem, shouldOpenSideCart });
    },
    [removeSingleProductsFromCart]
  );

  const setLuxeActive = useCallback(
    async (isLuxe, plan, cart, shouldOpenSideCart) => {
      const luxeLineItem = cart.lines.find(
        (l) =>
          l.variant.id ===
          `gid://shopify/ProductVariant/${plan.exison_plan_settings.value.product_variant_id}`
      );

      if (isLuxe) {
        if (typeof luxeLineItem === "undefined" && cart.lines.length > 0) {
          await addLuxeItemToCart(plan, shouldOpenSideCart);
        } else {
          setCanAutoAddLuxeInCart(true);
        }
      } else if (luxeLineItem) {
        await removeLuxeItemFromCart(luxeLineItem, shouldOpenSideCart);
      } else {
        setCanAutoAddLuxeInCart(false);
      }
    },
    [addLuxeItemToCart, removeLuxeItemFromCart]
  );

  /*
    Listen to location changes - for the auth search parameter
    i.e. http://localhost:8888/collections/bestsellers?luxe=true will open cart + activate luxe
    i.e. http://localhost:8888/collections/bestsellers?luxe=false will open cart + de-activate luxe
  */
  useEffect(() => {
    const queryParams = getAllUrlParams(location.href);
    if (queryParams.luxe) {
      setLuxeActive(queryParams.luxe === "true", subscriptionPlan, cart);
      setIsSideCartOpen(true);
    }

    if (
      queryParams.force_guest ||
      (queryParams?.utm_content && queryParams?.utm_content.includes("No Luxe"))
    ) {
      setIsForcedGuest(true);
    }
  }, [location, subscriptionPlan, cart, setLuxeActive, setIsSideCartOpen]);

  const refundLastCharge = useCallback(
    async ({ orderId, amount }) => {
      try {
        const res = await axios.post(
          "/.netlify/functions/subscription",
          {},
          {
            params: {
              cid: accountDetails.id.replace("gid://shopify/Customer/", ""),
              scid: luxe.customerSubscription.scid,
              hash: accountDetails.metafields
                .find((m) => m.key === "exison_hash")
                .value.replaceAll('"', ""),
              email: accountDetails.email,
              countryCode,
              action: "REFUND",
            },
          }
        );
        if (res.status !== 200) {
          return { status: "error", message: "Something went wrong, please try again later" };
        }
        if (res.data?.status === "success") {
          trackSubscriptionRefund({ orderId, amount });
        }
        return res.data;
      } catch (e) {
        sendALErrorReport(e, {
          name: "MembershipDetailsRefundSubscription",
          priority: "P3",
        });
        return false;
      }
    },
    [
      accountDetails?.email,
      accountDetails?.id,
      accountDetails?.metafields,
      countryCode,
      luxe?.customerSubscription?.scid,
      trackSubscriptionRefund,
    ]
  );

  // status can be "ACTIVE", "PAUSED", "CANCELLED"
  const updateSubscriptionStatus = useCallback((status) => {
    setLuxe((prevLuxe) => ({
      ...prevLuxe,
      customerSubscription: {
        ...prevLuxe.customerSubscription,
        status,
      },
    }));
  }, []);

  const updateSubscriptionNextBillingDate = useCallback((date) => {
    setLuxe((prevLuxe) => ({
      ...prevLuxe,
      customerSubscription: {
        ...prevLuxe.customerSubscription,
        next_billing: date,
      },
    }));
  }, []);

  const acceptChurnOffer = useCallback(
    async ({ scid, offerId }) => {
      if (!scid || !offerId || !accountDetails?.id || !accountDetails?.email) {
        throw new Error("Missing required parameters for accepting churn offer");
      }
      const params = new URLSearchParams({
        scid,
        countryCode,
        offerId,
        cid: accountDetails.id.replace("gid://shopify/Customer/", ""),
        email: accountDetails.email,
      });

      const response = await fetch(`/.netlify/functions/subscription-churn?${params}`, {
        method: "POST",
      });

      const text = await response.text();
      const isJson = text.startsWith("{") || text.startsWith("[");

      const data = isJson ? JSON.parse(text) : text;

      if (response.ok) {
        setHasActiveOffer(true);
        pullAccountDetails();
      }

      return {
        ok: response.ok,
        status: response.status,
        data,
      };
    },
    [countryCode, accountDetails?.id, accountDetails?.email, pullAccountDetails]
  );

  const cancelSubscription = useCallback(
    async (cancelReason = "unspecified") => {
      try {
        const res = await axios.post(
          "/.netlify/functions/subscription",
          {},
          {
            params: {
              cid: accountDetails.id.replace("gid://shopify/Customer/", ""),
              email: accountDetails.email,
              action: "CANCEL",
              reason: cancelReason,
            },
          }
        );
        if (res.status !== 200) {
          console.error("Failed to cancel subscription, status code:", res.status);
          throw new Error(`Failed to cancel subscription, status code: ${res.status}`);
        }
        if (res.data?.result === "error" || res.data?.error) {
          sendALErrorReport(new Error("Failed to cancel subscription, response indicated error"), {
            name: "MembershipDetailsCancelSubscription",
            priority: "P1",
          });
          return { status: "error", reason: "Something went wrong, please try again." };
        }
        const cancelledSubscription = await axios.get(`/.netlify/functions/subscription`, {
          params: {
            cid: accountDetails.id.replace("gid://shopify/Customer/", ""),
            email: accountDetails.email,
          },
        });
        if (cancelledSubscription.status === 200) {
          updateSubscriptionNextBillingDate(
            cancelledSubscription.data.membership.next_billing_date
          );
          updateSubscriptionStatus(cancelledSubscription.data.membership.status);
          trackSubscriptionCancel(cancelReason);
          return { status: "success" };
        }
        console.error("Failed to cancel subscription, unexpected response:", cancelledSubscription);
        return { status: "error", reason: "Something went wrong, please try again." };
      } catch (error) {
        console.error("Failed to cancel subscription:", error);
        sendALErrorReport(error, {
          name: "MembershipDetailsCancelSubscription",
          priority: "P1",
        });
        return { status: "error", reason: "Something went wrong, please try again." };
      }
    },
    [
      accountDetails?.id,
      accountDetails?.email,
      updateSubscriptionNextBillingDate,
      updateSubscriptionStatus,
      trackSubscriptionCancel,
    ]
  );

  const hasLuxeInCart = useMemo(
    () =>
      !!cart.lines.find(
        (l) =>
          l.variant.id ===
          `gid://shopify/ProductVariant/${subscriptionPlan.exison_plan_settings.value.product_variant_id}`
      ),
    [cart.lines, subscriptionPlan.exison_plan_settings.value.product_variant_id]
  );

  const isLuxeActive = luxe.customerSubscription?.status === "ACTIVE";
  const isLuxeEnabled = countryCode === Country.US;
  const shouldAddLuxeInNextATC =
    canAutoAddLuxeInCart && isLuxeEnabled && !cart.lines.length && !isForcedGuest;

  const luxeGiftInfo = getLuxeGiftInfo(luxe, accountDetails?.tags);
  const addLuxeGiftToCart = useAddLuxeGiftToCart(luxeGiftInfo?.status === "unlocked");
  useEffect(() => {
    if (isLoadingAuth || isLoadingCart) return;
    const giftInCart = cart.lines.find((l) =>
      l.attributes?.find(
        (attr) => attr.key === LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY && attr.value === "true"
      )
    );
    if (luxeGiftInfo?.status !== "unlocked" || !isLuxeActive || hasActiveOffer) {
      if (giftInCart) {
        removeSingleProductsFromCart({ lineToRemove: giftInCart, shouldOpenSideCart: false });
      }
      return;
    }
    if ((!giftInCart || giftInCart.quantity !== 1) && luxeGiftInfo.status === "unlocked") {
      addLuxeGiftToCart(giftInCart?.id);
    }
  }, [
    accountDetails,
    isLoadingAuth,
    cart,
    addLuxeGiftToCart,
    removeSingleProductsFromCart,
    isLoadingCart,
    luxeGiftInfo?.status,
    isLuxeActive,
    hasActiveOffer,
  ]);

  // // Set cart attributes if different from the current state
  const luxeCartAttribute = cart.attributes?.find((attr) => attr.key === "isLuxe");
  useEffect(() => {
    const isLuxe = isLuxeActive || hasLuxeInCart;
    if (!!cart && !cart.isDefaultObject && luxeCartAttribute?.value !== isLuxe.toString()) {
      updateCartAttributes({
        customAttributes: [
          ...(cart.attributes || []).filter(({ key }) => key !== "isLuxe"),
          { key: "isLuxe", value: isLuxe.toString() },
        ],
      });
    }
  }, [isLuxeActive, hasLuxeInCart, updateCartAttributes, luxeCartAttribute?.value, cart]);

  const amountSaved =
    accountDetails && accountDetails.metafields
      ? Number(
          accountDetails.metafields.find(
            (m) => m.namespace === "subscription" && m.key === "amount_saved"
          )?.value || ""
        )
      : null;

  const luxeData = useMemo(
    () => ({
      ...defaultLuxeContext,
      luxe: {
        ...defaultLuxeContext.luxe,
        ...luxe,
        isLuxeEnabled,
        isLuxeActive,
        isForcedGuest,
        shouldAddLuxeInNextATC,
        subscriptionPlan,
        hasLuxeInCart,
        subscriptionCredit,
        hasActiveOffer,
        isLoadingChurnOffer,
        luxePerks,
        amountSaved,
      },
      setLuxeActive,
      addLuxeItemToCart,
      removeLuxeItemFromCart,
      updateSubscriptionStatus,
      updateSubscriptionNextBillingDate,
      refundLastCharge,
      acceptChurnOffer,
      cancelSubscription,
      setHasActiveOffer,
    }),
    [
      luxe,
      setLuxeActive,
      updateSubscriptionStatus,
      addLuxeItemToCart,
      removeLuxeItemFromCart,
      isLuxeActive,
      isForcedGuest,
      hasLuxeInCart,
      subscriptionPlan,
      updateSubscriptionNextBillingDate,
      subscriptionCredit,
      isLuxeEnabled,
      shouldAddLuxeInNextATC,
      refundLastCharge,
      acceptChurnOffer,
      hasActiveOffer,
      setHasActiveOffer,
      isLoadingChurnOffer,
      luxePerks,
      amountSaved,
      cancelSubscription,
    ]
  );

  return <LuxeContext.Provider value={luxeData}>{children}</LuxeContext.Provider>;
}
export default withALErrorBoundary({
  name: "LuxeProvider",
  priority: "P1",
})(LuxeProvider);

LuxeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  subscription: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      namespace: PropTypes.string,
      type: PropTypes.string,
      value: PropTypes.string,
    })
  ),
};
